import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, UserPlus, Mail, Send, Copy, Check, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase/client';
import type { Ticket } from '@/types/tickets';

interface TicketAllocationProps {
  tickets: Ticket[];
  onClose: () => void;
  onComplete: () => void;
}

interface GuestInfo {
  name: string;
  email: string;
  phone?: string;
}

interface AllocationResult {
  ticketNumber: number;
  guestName: string;
  guestEmail: string;
  shareLink: string;
}

export function TicketAllocation({ tickets, onClose, onComplete }: TicketAllocationProps) {
  const [guests, setGuests] = useState<Record<string, GuestInfo>>(
    tickets.reduce((acc, ticket) => ({
      ...acc,
      [ticket.id]: { name: '', email: '', phone: '' }
    }), {})
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [allocationResults, setAllocationResults] = useState<AllocationResult[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const updateGuest = (ticketId: string, field: keyof GuestInfo, value: string) => {
    setGuests(prev => ({
      ...prev,
      [ticketId]: {
        ...prev[ticketId],
        [field]: value
      }
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Validate that all tickets have at least name and email
      const hasEmptyFields = tickets.some(ticket => {
        const guest = guests[ticket.id];
        return !guest.name || !guest.email;
      });

      if (hasEmptyFields) {
        setError('Please fill in name and email for all tickets');
        return;
      }

      // Update all tickets
      const results: AllocationResult[] = [];
      
      for (const ticket of tickets) {
        const guest = guests[ticket.id];
        
        // Generate claim token if not exists
        let claimToken = ticket.claim_token;
        if (!claimToken) {
          // Generate a simple claim token
          claimToken = `tk_${Math.random().toString(36).substr(2, 16)}`;
        }
        
        const { error: updateError } = await supabase
          .from('tickets')
          .update({
            guest_name: guest.name,
            guest_email: guest.email,
            guest_phone: guest.phone || null,
            transfer_type: 'allocation',
            claim_token: claimToken,
            updated_at: new Date().toISOString()
          })
          .eq('id', ticket.id);
          
        if (updateError) throw updateError;
        
        // Generate pretty share link
        const shareLink = `${window.location.origin}/claim/ticket/${claimToken}`;
        results.push({
          ticketNumber: ticket.ticket_number,
          guestName: guest.name,
          guestEmail: guest.email,
          shareLink
        });
      }
      
      setAllocationResults(results);
      setShowSuccess(true);
    } catch (err) {
      console.error('Allocation error:', err);
      setError(err instanceof Error ? err.message : 'Failed to allocate tickets');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = async (text: string, index: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const shareTicket = async (result: AllocationResult) => {
    const message = `Hi ${result.guestName}! 🎫\n\nYou've been allocated a ticket for an amazing experience on Rendezvous.\n\nClaim your ticket here:\n${result.shareLink}\n\nIMPORTANT: Sign up or log in using this email: ${result.guestEmail}\n\nNote: This is an allocated ticket. The purchaser retains ownership and can reclaim it if needed.`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Your Rendezvous Ticket',
          text: message
        });
      } catch (err) {
        // User cancelled or error - fallback to copy
        await navigator.clipboard.writeText(message);
        alert('Message copied to clipboard!');
      }
    } else {
      // Fallback for desktop
      await navigator.clipboard.writeText(message);
      alert('Message copied to clipboard!');
    }
  };

  const copyAllMessages = async () => {
    const message = allocationResults.map(result => 
      `🎫 Ticket #${result.ticketNumber} allocated to ${result.guestName}\n` +
      `📧 Email: ${result.guestEmail}\n` +
      `🔗 Claim your ticket: ${result.shareLink}\n`
    ).join('\n---\n\n');
    
    await navigator.clipboard.writeText(message);
    alert('All ticket information copied to clipboard!');
  };

  if (showSuccess) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-earth-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="p-4 border-b border-earth-700 flex items-center justify-between">
            <h2 className="text-xl font-display text-sand-100">Tickets Allocated Successfully!</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowSuccess(false);
                onComplete();
              }}
              className="text-sand-400 hover:text-sand-200"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="p-4 overflow-y-auto flex-1">
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 mb-6">
              <p className="text-green-400 font-medium mb-2">✅ All tickets have been allocated!</p>
              <p className="text-sand-400 text-sm">
                Share the links below with your guests so they can claim their tickets.
              </p>
            </div>

            <div className="space-y-4">
              {allocationResults.map((result, index) => (
                <div key={index} className="bg-earth-700/50 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-sand-100">
                        Ticket #{result.ticketNumber} - {result.guestName}
                      </h4>
                      <p className="text-sm text-sand-400">{result.guestEmail}</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-earth-900/50 rounded-lg">
                    <p className="text-xs text-sand-500 mb-2">Share this message:</p>
                    <div className="text-sm text-sand-300 font-mono break-all">
                      Hi {result.guestName}! You've been allocated a ticket. 
                      Click here to claim it: {result.shareLink}
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => shareTicket(result)}
                        className="flex-1"
                      >
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(
                          `Hi ${result.guestName}! 🎫\n\nYou've been allocated a ticket. Click here to claim it:\n${result.shareLink}\n\nSign in with: ${result.guestEmail}`,
                          index
                        )}
                        className="flex-1"
                      >
                        {copiedIndex === index ? (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 border-t border-earth-700 flex justify-between">
            <Button
              variant="outline"
              onClick={copyAllMessages}
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy All Messages
            </Button>
            <Button
              onClick={() => {
                setShowSuccess(false);
                onComplete();
              }}
            >
              Done
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-earth-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden"
      >
        <div className="p-4 border-b border-earth-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-sand-400" />
            <h2 className="text-xl font-display text-sand-100">Allocate Tickets to Guests</h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="text-sand-400 hover:text-sand-200"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <p className="text-sand-400 mb-6">
            Assign guest details to your tickets. You'll receive shareable links for each guest after allocation.
          </p>

          <div className="space-y-6">
            {tickets.map((ticket, index) => (
              <div key={ticket.id} className="bg-earth-700/50 p-4 rounded-lg">
                <h3 className="font-medium text-sand-100 mb-3">
                  Ticket #{ticket.ticket_number}
                </h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-sand-300 mb-1">
                      Guest Name *
                    </label>
                    <input
                      type="text"
                      value={guests[ticket.id].name}
                      onChange={(e) => updateGuest(ticket.id, 'name', e.target.value)}
                      placeholder="John Doe"
                      required
                      className="w-full px-3 py-2 bg-earth-700 border border-earth-600 rounded-lg text-sand-100 placeholder-sand-500 focus:ring-2 focus:ring-sand-400 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-sand-300 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={guests[ticket.id].email}
                      onChange={(e) => updateGuest(ticket.id, 'email', e.target.value)}
                      placeholder="john@example.com"
                      required
                      className="w-full px-3 py-2 bg-earth-700 border border-earth-600 rounded-lg text-sand-100 placeholder-sand-500 focus:ring-2 focus:ring-sand-400 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-sand-300 mb-1">
                      Phone Number (Optional)
                    </label>
                    <input
                      type="tel"
                      value={guests[ticket.id].phone}
                      onChange={(e) => updateGuest(ticket.id, 'phone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="w-full px-3 py-2 bg-earth-700 border border-earth-600 rounded-lg text-sand-100 placeholder-sand-500 focus:ring-2 focus:ring-sand-400 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-earth-700 flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>Allocating...</>
            ) : (
              <>
                <UserPlus className="h-4 w-4 mr-2" />
                Allocate & Get Links
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
} 